import { z } from "zod";

import type { EmbeddedStudioDefaultProps } from "@mountayaapp/types/api/api";

import type { PlanTier } from "../store/identity";
import { DEFAULT_TERRAIN, terrainVariantSchema } from "./searchParams";

/**
 * studioConfigSchema is the fully-resolved control-visibility configuration the
 * in-app studio consumes. It mirrors embedConfigSchema's resolved shape but
 * carries only the flags the internal controls read — there are no URL
 * overrides and no `embedded` marker (the in-app studio is never embedded).
 * Zod-first so resolution output is validated and the StudioConfig type is
 * derived from it.
 */
export const studioConfigSchema = z.object({
  terrain: z.string(),
  terrainVariant: terrainVariantSchema,
  withDirectionsControl: z.boolean(),
  withDownloadControl: z.boolean(),
  withNavigationControl: z.boolean(),
  withOverlays: z.array(z.string()),
  withOverlaysControl: z.boolean(),
  withPreferencesControl: z.boolean(),
  withProOverlays: z.boolean(),
  withStreetviewControl: z.boolean(),
  withTerrains: z.array(z.string()),
});

/**
 * StudioConfig is the fully-resolved in-app studio control-visibility
 * configuration, derived from studioConfigSchema.
 */
export type StudioConfig = z.infer<typeof studioConfigSchema>;

/**
 * resolveStudioConfig folds the root organization's default_props and the
 * viewer's plan tier into the fully-resolved StudioConfig the in-app studio
 * threads to its controls. It is render-free and pure.
 *
 * When the root org resolves, every control flag, terrain, and allowlist comes
 * straight from default_props; Street View is additionally AND-gated by the Pro
 * plan (a free viewer never sees the pegman even when the org enables it). When
 * no root org resolves (orgDefaultProps null), it falls back to a full-featured
 * default with Street View still Pro-gated. The result is parsed through
 * studioConfigSchema so resolution output is validated.
 *
 * withProOverlays is the plan side of the layers panel, and it gates that panel's whole
 * Pro tier: the Pro overlay cards (every overlay in the manifest carries a `plan`, and
 * the tile API refuses a Pro one to a free viewer), the Pro enrichment switches, and the
 * timestamp scrubber (the tile API honors a non-now timestamp only for Pro). Resolving it
 * here from the SAME plan tier keeps the panel honest — it offers exactly what the tile
 * API will actually serve this viewer, rather than controls that fail on use.
 */
export const resolveStudioConfig = (
  orgDefaultProps: EmbeddedStudioDefaultProps | null,
  planTier: PlanTier,
): StudioConfig => {
  const isPro = planTier !== "free";

  if (!orgDefaultProps) {
    return studioConfigSchema.parse({
      terrain: DEFAULT_TERRAIN,
      terrainVariant: "light",
      withDirectionsControl: false,
      withDownloadControl: false,
      withNavigationControl: true,
      withOverlays: [],
      withOverlaysControl: true,
      withPreferencesControl: true,
      withProOverlays: isPro,
      withStreetviewControl: isPro,
      withTerrains: [],
    });
  }

  return studioConfigSchema.parse({
    terrain: orgDefaultProps.terrain,
    terrainVariant: orgDefaultProps.terrain_variant,
    withDirectionsControl: orgDefaultProps.with_directions_control,
    withDownloadControl: orgDefaultProps.with_download_control,
    withNavigationControl: orgDefaultProps.with_navigation_control,
    withOverlays: orgDefaultProps.with_overlays,
    withOverlaysControl: orgDefaultProps.with_overlays_control,
    withPreferencesControl: orgDefaultProps.with_preferences_control,
    withProOverlays: isPro,
    withStreetviewControl: orgDefaultProps.with_streetview_control && isPro,
    withTerrains: orgDefaultProps.with_terrains,
  });
};
